import { Store, ExternalLink, Eye } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { Card, CardLabel } from "@/components/ui/card";
import { marketplaceLabel } from "@/lib/marketplace";
import { cn } from "@/lib/utils";
import type { Link as LinkType } from "@/lib/types";

// ---------------------------------------------------------------------------
// FeaturedLinks - cards de produtos em destaque (estilo easyfy)
// ---------------------------------------------------------------------------

interface FeaturedLinksProps {
  links: LinkType[];
  isLoading?: boolean;
  className?: string;
}

const PLATFORM_COLORS: Record<string, string> = {
  amazon: "bg-[hsl(42_80%_57%/0.15)] text-[hsl(42_80%_30%)]",
  mercadolivre: "bg-[hsl(200_70%_50%/0.15)] text-[hsl(200_70%_30%)]",
  shopee: "bg-[hsl(330_70%_55%/0.15)] text-[hsl(330_70%_35%)]",
  magalu: "bg-[hsl(150_54%_51%/0.15)] text-[hsl(150_54%_30%)]",
  aliexpress: "bg-[hsl(270_60%_60%/0.15)] text-[hsl(270_60%_35%)]",
  awin: "bg-[hsl(0_0%_50%/0.15)] text-[hsl(0_0%_30%)]",
  shein: "bg-[hsl(340_80%_55%/0.15)] text-[hsl(340_80%_35%)]",
};

function platformBadgeColor(code: string): string {
  return PLATFORM_COLORS[code.toLowerCase()] ?? "bg-[hsl(var(--border-strong))] text-muted-foreground";
}

function FeaturedLinks({ links, isLoading, className }: FeaturedLinksProps): JSX.Element {
  // Mostra ate 4 links mais recentes como destaque
  const topLinks = links.slice(0, 4);

  return (
    <Card variant="raised" className={cn("p-5 flex flex-col gap-4", className)}>
      <div className="flex items-center justify-between">
        <CardLabel>Links em Destaque</CardLabel>
        <Store size={14} className="text-muted-foreground" />
      </div>

      {isLoading ? (
        <div className="animate-pulse space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-lg bg-[hsl(var(--border-strong))]" />
              <div className="flex-1 space-y-1.5">
                <div className="h-3 w-32 rounded bg-[hsl(var(--border-strong))]" />
                <div className="h-2 w-20 rounded bg-[hsl(var(--border-strong)/0.6)]" />
              </div>
            </div>
          ))}
        </div>
      ) : topLinks.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {topLinks.map((link) => (
            <div
              key={link.id}
              className="flex flex-col gap-2 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-3 transition-all hover:border-[hsl(var(--border-strong))] hover:shadow-sm"
            >
              {/* Plataforma badge */}
              <div className="flex items-center justify-between">
                <span
                  className={cn(
                    "inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider",
                    platformBadgeColor(link.marketplace_code),
                  )}
                >
                  {marketplaceLabel(link.marketplace_code)}
                </span>
                <a
                  href={link.destination_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  aria-label="Abrir link externo"
                >
                  <ExternalLink size={12} />
                </a>
              </div>

              {/* URL ou info do produto */}
              <p className="text-xs text-foreground font-medium leading-snug line-clamp-2">
                {new URL(link.destination_url).hostname.replace("www.", "")}
              </p>

              {/* Status / modo */}
              <div className="flex items-center gap-2 mt-auto">
                <span className="text-[10px] font-mono uppercase text-muted-foreground">
                  {link.mode === "shortener" ? "Encurtado" : "Deep Link"}
                </span>
                {link.short_code && (
                  <span className="text-[10px] font-mono text-muted-foreground">
                    /{link.short_code}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center gap-2 py-4 text-center">
          <Store size={20} className="text-muted-foreground" />
          <p className="text-xs text-muted-foreground">Nenhum link gerado ainda</p>
        </div>
      )}

      {topLinks.length > 0 && (
        <Link
          to="/app/links"
          className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-[hsl(var(--border))] px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground hover:border-[hsl(var(--border-strong))] transition-all"
        >
          <Eye size={12} />
          Ver todos os links
        </Link>
      )}
    </Card>
  );
}

export { FeaturedLinks };
